import React, { useState, useEffect } from 'react';
import './Rankings2026.css';

const FINALS_SPOTS = 8;

function FormBadge({ result }) {
  return (
    <span className={`form-badge form-${result.toLowerCase()}`}>{result}</span>
  );
}

function Rankings2026() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [sortBy, setSortBy] = useState('ladder_position');

  useEffect(() => {
    fetch('/rankings_2026.json')
      .then(r => {
        if (!r.ok) throw new Error('not found');
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  if (loading) {
    return <div className="rankings-container"><div className="loading" style={{ minHeight: '400px' }}>Loading rankings...</div></div>;
  }

  if (error || !data) {
    return (
      <div className="rankings-container">
        <div className="season-empty">
          <h2>Current &amp; Predicted Ladder 2026</h2>
          <p>No rankings data yet. Run <code>make rankings</code> to generate.</p>
        </div>
      </div>
    );
  }

  const sorted = [...data.teams].sort((a, b) => {
    if (sortBy === 'predicted_total_wins') return b.predicted_total_wins - a.predicted_total_wins;
    if (sortBy === 'finals_probability') return (b.finals_probability ?? 0) - (a.finals_probability ?? 0);
    return a.ladder_position - b.ladder_position;
  });

  return (
    <div className="rankings-container">
      <div className="season-header">
        <h2>Current &amp; Predicted Ladder 2026</h2>
        <p className="season-subtitle">
          After {data.current_round} · Model predictions for remaining {data.teams[0]?.predicted_total_wins != null ? 'season' : ''}
          {data.simulations > 0 ? ` · Finals % from ${data.simulations.toLocaleString()} simulations` : ''}
        </p>
      </div>

      <div className="rankings-sort-bar">
        <span className="rankings-sort-label">Sort by:</span>
        {[
          { key: 'ladder_position', label: 'Current ladder' },
          { key: 'predicted_total_wins', label: 'Predicted wins' },
          { key: 'finals_probability', label: 'Finals %' },
        ].map(opt => (
          <button
            key={opt.key}
            className={`sort-btn ${sortBy === opt.key ? 'active' : ''}`}
            onClick={() => setSortBy(opt.key)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="rankings-table-wrap">
        <table className="rankings-table">
          <thead>
            <tr>
              <th className="col-pos">#</th>
              <th className="col-team">Team</th>
              <th className="col-num">W</th>
              <th className="col-num">L</th>
              <th className="col-num">Pts</th>
              <th className="col-num">Pct</th>
              <th className="col-num col-highlight">Pred W</th>
              <th className="col-num col-highlight">Pred #</th>
              <th className="col-num col-highlight">Finals %</th>
              <th className="col-form">Form</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((team, idx) => {
              const inFinalsZone = team.finals_probability != null
                ? team.finals_probability >= 0.5
                : team.ladder_position <= FINALS_SPOTS;
              const isEighthCutoff = idx < sorted.length - 1 &&
                sortBy === 'ladder_position' &&
                team.ladder_position === FINALS_SPOTS;

              return (
                <React.Fragment key={team.team}>
                  <tr className={`rankings-row ${inFinalsZone ? 'finals-zone' : ''}`}>
                    <td className="col-pos">
                      <span className={`pos-num ${inFinalsZone ? 'pos-finals' : ''}`}>
                        {team.ladder_position}
                      </span>
                    </td>
                    <td className="col-team">{team.team}</td>
                    <td className="col-num">{team.wins}</td>
                    <td className="col-num">{team.losses}</td>
                    <td className="col-num">{team.points}</td>
                    <td className="col-num">{team.percentage != null ? `${team.percentage}%` : '—'}</td>
                    <td className="col-num col-highlight">
                      <strong>{team.predicted_total_wins}</strong>
                    </td>
                    <td className="col-num col-highlight">
                      <span className={`pos-num ${team.predicted_ladder_position <= 8 ? 'pos-finals' : ''}`}>
                        {team.predicted_ladder_position}
                      </span>
                    </td>
                    <td className="col-num col-highlight">
                      {team.finals_probability != null
                        ? <span className={`finals-prob ${team.finals_probability >= 0.5 ? 'prob-high' : team.finals_probability >= 0.2 ? 'prob-mid' : 'prob-low'}`}>
                            {(team.finals_probability * 100).toFixed(0)}%
                          </span>
                        : '—'}
                    </td>
                    <td className="col-form">
                      <div className="form-badges">
                        {team.recent_form.length > 0
                          ? team.recent_form.map((r, i) => <FormBadge key={i} result={r} />)
                          : <span className="form-none">—</span>}
                      </div>
                    </td>
                  </tr>
                  {isEighthCutoff && (
                    <tr className="cutoff-row" key="cutoff">
                      <td colSpan={10}><div className="cutoff-line" /></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="rankings-footer">
        Pred W = actual wins + Phase Model expected wins from remaining fixture ·
        Pred # = predicted ladder position · Finals % = top-8 finish probability from Monte Carlo simulation
      </p>
    </div>
  );
}

export default Rankings2026;
